import { createGlobalStyle } from "styled-components";

export const GlobalStyle = createGlobalStyle`
:root {
  /* The client's design system — docs/design_handoff/DESIGN-SYSTEM.md,
     the "Ink" (dark, default) theme. This used to be the video slide
     generator's palette; it is now the client's, so the site and the app
     people land in agree. Contrast is computed against --surface, the
     tighter of the two grounds — check new values there, not --ground. */

  /* The three official colours. Exact and non-negotiable. */
  --old-glory-blue: #002868;                 /* PMS 281 C */
  --old-glory-red: #BF0A30;                  /* PMS 193 C */
  --white: #FFFFFF;

  /* Grounds. Old Glory Blue is a canton, not the whole cloth — at full
     saturation it is unreadable under dense text, so it holds mastheads
     and seals while the reading ground is a deepened navy drawn from it. */
  --ground: #071224;
  --surface: #0d1c38;
  --raised: #15274a;
  --sunken: #040b17;
  --line: #22355e;
  --line-soft: #132441;

  --fg: #f2f5fa;
  --fg-dim: #9fb0cc;
  --fg-mute: #8494b3;

  /* Brass is the accent. The site's --brass used to alias to pale blue;
     it is now actually brass, which is the loudest change here. */
  --brass-accent: #c9a227;
  --brass-ink: #171204;

  --good: #4a9d8f;
  --warn: #c9a227;
  --bad: #BF0A30;
  --info: #5b8fd6;
  --paper: #f2ebdc;
  --paper-ink: #2a2418;
  --paper-line: #d8cdb4;

  /* Four voices, only ever two on screen at once. Newsreader is requested
     WITHOUT the opsz axis — with opsz in the tuple the face silently fails
     to load and falls back to Times. */
  --face-display: Spectral, Georgia, serif;  /* engraved capitals */
  --face-ui: "Public Sans", -apple-system, BlinkMacSystemFont, "Segoe UI",
    Roboto, sans-serif;                      /* all UI and dense text */
  --face-prose: Newsreader, Georgia, serif;  /* the reading voice */
  --face-mono: "IBM Plex Mono", SFMono-Regular, Consolas, Menlo, monospace;

  /* Engraved capitals, as one declaration. */
  --engraved-tracking: 0.19em;

  --flag-rule-height: 5px;
  --flag-rule: linear-gradient(
    180deg,
    var(--white) 0 1px,
    var(--old-glory-red) 1px 4px,
    var(--white) 4px 5px
  );

  /* The site's existing names, repointed onto the system above so every
     existing rule repaints without a rewrite. */
  --ink: var(--ground);
  --ink-deep: var(--sunken);
  --ink-rule: var(--line-soft);

  --card: var(--white);                      /* flag white — card stock */
  --card-ink: #0b1830;                       /* Marble --fg */
  --card-ink-dim: #42506b;                   /* Marble --fg-dim */

  --accent: var(--fg-dim);
  --accent-strong: var(--white);             /* CTA fill */
  --flag-red: var(--old-glory-red);
  --flag-red-lit: #FF6B7F;                   /* lifted, for red on the navy ground */

  --chalk: var(--fg);
  --chalk-dim: var(--fg-mute);

  --vellum: var(--card);
  --vellum-dim: var(--card-ink-dim);
  --brass: var(--brass-accent);
  --verdigris: var(--good);
}

html {
  box-sizing: border-box;
  font-size: 100%;
  -webkit-text-size-adjust: 100%;
  overflow-y: scroll;
}

*, *:before, *:after { box-sizing: inherit; }

body {
  margin: 0;
  background-color: var(--ink);
  color: var(--chalk);
  font-family: var(--face-ui);
  font-weight: 400;
  line-height: 1.6;
  font-kerning: normal;
  font-feature-settings: "kern", "liga", "clig", "calt";
  -webkit-font-smoothing: antialiased;
}

h1, h2, h3, h4 {
  margin: 0;
  line-height: 1.15;
  font-weight: 400;
}

p { margin: 0 0 1.15rem; }
p:last-child { margin-bottom: 0; }

ul { margin: 0; padding: 0; list-style: none; }

img { max-width: 100%; border-style: none; }

a {
  color: var(--verdigris);
  text-decoration: none;
  border-bottom: 1px solid rgba(78, 158, 143, 0.4);
  transition: color 160ms ease, border-color 160ms ease;
}

a:hover { color: var(--brass); border-bottom-color: var(--brass); }

a:focus-visible,
label:focus-within,
input:focus-visible + label {
  outline: 2px solid var(--brass);
  outline-offset: 3px;
}

/* The flag rule — a red band held off the blue field by a white hairline,
   which is the whole "red never touches blue" rule in one element. Recipe
   is the client's, unchanged. Exposed as a variable too, because section
   dividers draw it from a pseudo-element rather than a real node. */
.flagrule {
  height: var(--flag-rule-height);
  background: var(--flag-rule);
}

code, pre, samp, kbd {
  font-family: var(--face-mono);
}

pre { margin: 0; }

::selection { background: var(--brass); color: var(--ink-deep); }

@media (prefers-reduced-motion: reduce) {
  *, *:before, *:after {
    animation-duration: 0.001ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.001ms !important;
  }
}
`;
